// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO, ArticleVO } from '../model/vo/article.vo';
import { ArticleUsageUtils } from './article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '../constants/article-usage-info-property-def';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { PropertyUtils } from './property-utils';
import { ArticleBehaviorPropertyDef } from '../constants/article-behavior-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleManager } from '../manager/article.manager';
import { Dictionary } from 'typescript-collections';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { MetaElementUtils } from '../components/connections/connection-creation/meta-element.utils';

@Injectable({providedIn: 'root'})
export class CompositeArticleInfoUtils {

  constructor(protected containerService: ContainerService,
              private articleManager: ArticleManager) {
  }

  public isCompositePartArticle(articleContainer: ArticleContainer, useStrippedCompositeInfo: boolean = false): boolean {
    if (!articleContainer) {
      return false;
    }
    return ArticleUsageUtils.isCompositePartArticleUsage(articleContainer.articleUsage, useStrippedCompositeInfo);
  }

  public isBaseCompositePartArticle(compositePartArticle: ArticleContainer): boolean {
    const compositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(compositePartArticle);
    if (!compositeArticleUsageId) {
      return false;
    }

    const parentArticleGUIDs: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(compositePartArticle.articleUsage);
    if (parentArticleGUIDs.length === 0) {
      return true;
    }

    const parentArticle: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentArticleGUIDs[0]);
    return !parentArticle || CompositeArticleInfoUtils.getCompositeArticleUsageId(parentArticle) !== compositeArticleUsageId;
  }



  public isPartArticlesOfSameCompositeArticle(partArticle1: ArticleContainer, partArticle2: ArticleContainer): boolean {
    const compositeArticleUsageId1: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(partArticle1);
    const compositeArticleUsageId2: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(partArticle2);
    return compositeArticleUsageId1 === compositeArticleUsageId2;
  }

  public isCompositePartArticleStickTogether(compositePartArticleContainer: ArticleContainer): boolean {
    const compositeArticle: ArticleVO = this.getCompositeArticleFromContainer(compositePartArticleContainer);
    return this.isCompositeArticleStickTogether(compositeArticle);
  }

  public getCompositeArticleFromContainer(compositePartArticleContainer: ArticleContainer): ArticleVO {
    if (!compositePartArticleContainer) {
      return null;
    }
    return this.getCompositeArticleFromUsage(compositePartArticleContainer.articleUsage);
  }

  public isCompositeArticleStickTogether(compositeArticle: ArticleVO): boolean {
    if (!compositeArticle) {
      return false;
    }

    // if the composite article contains less than 2 giving connections
    // then it's not a new extended composite article with multiple giving connections
    // therefore the result is FALSE since this property can be declared only for new extended composite articles
    if (!this.isArticleWithMultipleGivingConnections(compositeArticle)) {
      return false;
    }

    if (PropertyUtils.containsProperty(compositeArticle.behaviorProperties, ArticleBehaviorPropertyDef.IS_COMPOSITE_ARTICLE_STICK_TOGETHER)) {
      return PropertyUtils.getBooleanPropertyValue(
        compositeArticle.behaviorProperties,
        ArticleBehaviorPropertyDef.IS_COMPOSITE_ARTICLE_STICK_TOGETHER
      );
    }

    // if there is no such behavior property in the composite article, then the default value is true
    return true;
  }

  public isArticleWithMultipleGivingConnections(article: ArticleVO): boolean {
    const connectionsInSets: Dictionary<string, MetaElementVO[]> = MetaElementUtils.getConnectionsInSets(article.metaElements);
    for (const connectionsInSet of connectionsInSets.values()) {
      if (connectionsInSet.length > 1) {
        return true;
      }
    }
    return false;
  }

  public getCompositeArticleFromUsage(compositePartUsage: ArticleUsageVO, useStrippedCompositeInfo: boolean = false): ArticleVO | null {
    if (!compositePartUsage) {
      return null;
    }
    let article: ArticleVO = null;
    const articleId: number = this.getCompositeArticleId(compositePartUsage, useStrippedCompositeInfo);
    const articleVersion: number = this.getCompositeArticleVersion(compositePartUsage, useStrippedCompositeInfo);
    if (NumberUtils.isNumber(articleId) && NumberUtils.isNumber(articleVersion)) {
      article = this.articleManager.getCachedArticle(articleId, articleVersion);
    }
    if (!article) {
      article = this.getCompositeArticleFromUsageByArticleNo(compositePartUsage);
    }
    return article;
  }

  public getCompositeArticleId(compositePartUsage: ArticleUsageVO, useStrippedCompositeInfo: boolean = false): number {
    if (!compositePartUsage) {
      return NaN;
    }
    return ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      compositePartUsage,
      useStrippedCompositeInfo ? ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID : ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID
    );
  }

  public getCompositeArticleVersion(compositePartUsage: ArticleUsageVO, useStrippedCompositeInfo: boolean = false): number {
    if (!compositePartUsage) {
      return NaN;
    }
    return ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      compositePartUsage,
      useStrippedCompositeInfo
        ? ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION
        : ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION
    );
  }

  public getCompositeArticleFromUsageByArticleNo(compositePartUsage: ArticleUsageVO): ArticleVO | null {
    const compositeArticleNo: string = this.getCompositeArticleNoFromUsage(compositePartUsage);
    if (!compositeArticleNo) {
      return null;
    }
    return this.articleManager.getCachedArticleByArticleNo(compositeArticleNo);
  }

  public getCompositeArticleNoFromUsage(articleUsage: ArticleUsageVO): string {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO);
  }


  public static getCompositeArticleUsageId(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): string {
    if (compositePartArticle) {
      return ArticleUsageUtils.getRelatedCompositeArticleUsageId(compositePartArticle.articleUsage, useStrippedCompositeInfo);
    }
    return null;
  }
}
